import { useEffect, useMemo, useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Plus, Trash2, ExternalLink, Eye, EyeOff, Megaphone, Footprints, Share2, Link2, Scale, Phone,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { siteLinksAPI } from '../../services/api';

const GROUPS = [
  { key: 'banner',          label: 'Promo Banner',      icon: Megaphone,  hint: 'Each row is a rotating slide in the top banner. Label = the text shown. Leave URL empty for plain text, or add /products or https://… to make the entire slide clickable. Drag to reorder.' },
  { key: 'footer_quick',    label: 'Footer · Quick Links', icon: Link2,    hint: 'Top column of links in the footer.' },
  { key: 'footer_support',  label: 'Footer · Support',  icon: Footprints, hint: 'Second column — installation, FAQs, warranty, returns.' },
  { key: 'footer_social',   label: 'Footer · Social',   icon: Share2,     hint: 'Social icons in the footer. Label is used to pick the icon (Facebook/Twitter/Instagram/LinkedIn/Youtube).' },
  { key: 'footer_legal',    label: 'Footer · Legal',    icon: Scale,      hint: 'Bottom strip · privacy/terms links.' },
  { key: 'footer_contact',  label: 'Footer · Contact',  icon: Phone,      hint: 'Contact column. Leave URL blank for plain text (address). Use mailto:you@example.com for email, tel:+61... for phone.' },
];

const isExternal = (url) => /^https?:\/\//i.test(url || '');

const SortableRow = ({ link, onChange, onRemove }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-12 gap-2 items-center bg-white border rounded-md p-2 ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
      data-testid={`sitelink-row-${link.id}`}
    >
      <button
        className="col-span-1 flex items-center justify-center text-gray-400 cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        type="button"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <Input
        className="col-span-3"
        value={link.label}
        onChange={(e) => onChange({ ...link, label: e.target.value })}
        placeholder="Label"
        data-testid={`sitelink-label-${link.id}`}
      />

      <Input
        className="col-span-4 font-mono text-xs"
        value={link.url}
        onChange={(e) => onChange({ ...link, url: e.target.value })}
        placeholder="/products or https://…"
        data-testid={`sitelink-url-${link.id}`}
      />

      <div className="col-span-2 flex items-center gap-2">
        <Switch
          checked={link.target === '_blank'}
          onCheckedChange={(v) => onChange({ ...link, target: v ? '_blank' : '_self' })}
          aria-label="Open in new tab"
          data-testid={`sitelink-target-${link.id}`}
        />
        {isExternal(link.url) && <ExternalLink className="h-3.5 w-3.5 text-gray-400" title="External URL" />}
      </div>

      <div className="col-span-1 flex justify-center">
        <button
          type="button"
          onClick={() => onChange({ ...link, enabled: !link.enabled })}
          className={`flex items-center gap-1 text-xs ${link.enabled ? 'text-emerald-700' : 'text-gray-400'}`}
          title={link.enabled ? 'Visible — click to hide' : 'Hidden — click to show'}
          data-testid={`sitelink-enabled-${link.id}`}
        >
          {link.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
      </div>

      <div className="col-span-1 flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
          onClick={() => onRemove(link)}
          aria-label="Delete link"
          data-testid={`sitelink-delete-${link.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const GroupSection = ({ group, links, onChange, onAdd, onRemove, onReorder, saving, dirty }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = links.findIndex((l) => l.id === active.id);
    const newIndex = links.findIndex((l) => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(links, oldIndex, newIndex).map((l, i) => ({ ...l, order: i }));
    onReorder(group.key, reordered);
  };

  const Icon = group.icon;
  return (
    <Card data-testid={`sitelink-group-${group.key}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Icon className="h-5 w-5 text-blue-600" /> {group.label}
              {dirty && <Badge className="bg-amber-100 text-amber-700">Unsaved</Badge>}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{group.hint}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAdd(group.key)}
            data-testid={`sitelink-add-${group.key}`}
          >
            <Plus className="h-4 w-4 mr-1" /> Add link
          </Button>
        </div>

        {links.length === 0 ? (
          <p className="text-sm text-gray-500 italic p-4 text-center border-2 border-dashed rounded-md">
            No links yet — click <strong>Add link</strong> to create one.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <span className="col-span-1"></span>
              <span className="col-span-3">Label</span>
              <span className="col-span-4">URL / Path</span>
              <span className="col-span-2">New tab?</span>
              <span className="col-span-1 text-center">Show</span>
              <span className="col-span-1"></span>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={links.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {links.map((l) => (
                    <SortableRow
                      key={l.id}
                      link={l}
                      onChange={(updated) => onChange(group.key, updated)}
                      onRemove={(toRemove) => onRemove(group.key, toRemove)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {dirty && (
          <div className="text-xs text-amber-600 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            You have unsaved changes in this section.
          </div>
        )}

        <div className="flex justify-end pt-1 border-t border-gray-100 mt-2">
          <Button
            className={dirty ? 'bg-blue-600 hover:bg-blue-700' : ''}
            variant={dirty ? 'default' : 'outline'}
            onClick={() => saving.commit(group.key)}
            disabled={!dirty || saving.busy === group.key}
            data-testid={`sitelink-save-${group.key}`}
          >
            {saving.busy === group.key
              ? 'Saving…'
              : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SiteLinksAdmin = () => {
  const [data, setData] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await siteLinksAPI.adminList();
        if (cancelled) return;
        const normalised = {};
        GROUPS.forEach((g) => { normalised[g.key] = (d[g.key] || []).map((l, i) => ({ ...l, order: i })); });
        setData(normalised);
        setOriginal(JSON.parse(JSON.stringify(normalised)));
      } catch {
        if (!cancelled) toast.error('Failed to load site links');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirtyGroups = useMemo(() => {
    const result = {};
    GROUPS.forEach((g) => {
      result[g.key] = JSON.stringify(data[g.key] || []) !== JSON.stringify(original[g.key] || []);
    });
    return result;
  }, [data, original]);

  const updateLink = (groupKey, updated) => {
    setData((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).map((l) => (l.id === updated.id ? updated : l)),
    }));
  };

  const reorderGroup = (groupKey, reordered) => {
    setData((prev) => ({ ...prev, [groupKey]: reordered }));
  };

const addLink = (groupKey) => {
    // Deferred: create a local placeholder with a temp id (marked `_isNew`).
    // It will be POSTed to the API when the user clicks Save changes.
    const order = (data[groupKey] || []).length;
    const tempId = `new-${Math.random().toString(36).slice(2, 10)}`;
    const newLink = {
      id: tempId,
      group: groupKey,
      label: 'New link',
      url: '/',
      target: '_self',
      enabled: true,
      order,
      _isNew: true,
    };
    setData((prev) => ({ ...prev, [groupKey]: [...(prev[groupKey] || []), newLink] }));
  };

  const removeLink = (groupKey, link) => {
    if (!window.confirm(`Delete "${link.label}"?`)) return;
    setData((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).flatMap((l) => {
        if (l.id !== link.id) return [l];
        // New links (not yet persisted) can be dropped entirely
        if (l._isNew) return [];
        // Existing links are marked for deletion; hidden from the UI but
        // remain in state so we can DELETE them at commit time.
        return [{ ...l, _deleted: true }];
      }),
    }));
  };

  const commitGroup = async (groupKey) => {
    setBusy(groupKey);
    try {
      const links = data[groupKey] || [];
      const originalLinks = original[groupKey] || [];
      const origById = Object.fromEntries(originalLinks.map((l) => [l.id, l]));

      // 1) Deletions (existing links flagged for removal)
      for (const l of links.filter((x) => x._deleted && !x._isNew)) {
        await siteLinksAPI.remove(l.id);
      }

      // 2) Inserts (temp-id links) — replace temp id with real id on success
      const inserted = {};
      for (const l of links.filter((x) => x._isNew && !x._deleted)) {
        const { _isNew, _deleted, id: _tempId, ...payload } = l;
        const real = await siteLinksAPI.create(payload);
        inserted[_tempId] = real;
      }

      // 3) Updates (existing links whose values changed)
      for (const l of links) {
        if (l._isNew || l._deleted) continue;
        const orig = origById[l.id];
        if (!orig) continue;
        const diff = {};
        ['label', 'url', 'target', 'enabled'].forEach((f) => {
          if (l[f] !== orig[f]) diff[f] = l[f];
        });
        if (Object.keys(diff).length > 0) {
          await siteLinksAPI.update(l.id, diff);
        }
      }

      // 4) Reorder with the final set of real ids
      const finalLinks = links
        .filter((l) => !l._deleted)
        .map((l) => (inserted[l.id] ? { ...inserted[l.id], order: l.order } : l));
      await siteLinksAPI.reorder(
        groupKey,
        finalLinks.map((l, i) => ({ id: l.id, order: i })),
      );

      // 5) Sync local state to the persisted snapshot
      const clean = finalLinks.map((l, i) => {
        // strip transient flags
        const { _isNew, _deleted, ...rest } = l;
        return { ...rest, order: i };
      });
      setData((prev) => ({ ...prev, [groupKey]: clean }));
      setOriginal((prev) => ({ ...prev, [groupKey]: JSON.parse(JSON.stringify(clean)) }));
      toast.success(`${GROUPS.find((g) => g.key === groupKey).label} saved`);
    } catch (e) {
      console.error('[site-links] save failed', e);
      toast.error('Save failed');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading site links…</div>;
  }

  return (
    <div className="space-y-6" data-testid="sitelinks-admin">
      <div>
        <h2 className="text-2xl font-bold">Navigation & Links</h2>
        <p className="text-sm text-gray-600 mt-1">
          Edit any link displayed in the promo banner and the footer.
          Drag <GripVertical className="inline h-3.5 w-3.5 align-middle text-gray-400" /> to reorder.
          Use the eye to hide a link without deleting it.
        </p>
      </div>

      {GROUPS.map((g) => (
        <GroupSection
          key={g.key}
          group={g}
          links={(data[g.key] || []).filter((l) => !l._deleted)}
          onChange={updateLink}
          onAdd={addLink}
          onRemove={removeLink}
          onReorder={reorderGroup}
          saving={{ busy, commit: commitGroup }}
          dirty={dirtyGroups[g.key]}
        />
      ))}
    </div>
  );
};

export default SiteLinksAdmin;
